import { api } from './api';

export type AchievementStatus = 'locked' | 'available' | 'in_progress' | 'completed';

export interface AchievementRule {
  type: 'event_first' | 'event_threshold' | 'event_count_total' | 'event_count_window' | 'manual';
  event: string;
  field?: string;
  op?: 'gte' | 'gt' | 'lte' | 'lt' | 'eq';
  value?: number;
  count?: number;
  window?: string;
  match?: Record<string, string | number | boolean>;
}

export interface Achievement {
  id: string;
  key: string;
  title: string;
  description: string;
  iconUrl: string | null;
  tier: number;
  order: number;
  rule: AchievementRule;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Assignment {
  id: string;
  achievementId: string;
  tierSnapshot: number;
  progress: { count?: number; thresholdReached?: boolean; eventTimestamps?: string[] };
  completedAt: string | null;
  assignedAt: string;
}

export interface UserAchievementView {
  achievement: Achievement;
  assignment: Assignment | null;
  status: AchievementStatus;
  progressPercent: number;
}

export interface StreakView {
  type: string;
  current: number;
  longest: number;
  lastIncrementAt: string | null;
}

export interface UserAchievementsResponse {
  currentTier: number;
  achievements: UserAchievementView[];
  streaks: StreakView[];
}

export interface UnseenAchievement {
  assignmentId: string;
  achievement: Achievement;
  completedAt: string;
}

export interface RoadmapStep {
  achievement: Achievement;
  assignment: Assignment | null;
  progressPercent: number;
  position: number;
}

export interface Roadmap {
  currentTier: number;
  totalSteps: number;
  remainingSteps: number;
  steps: RoadmapStep[];
}

export const achievementsApi = {
  getMine: () => api.get<UserAchievementsResponse>('/achievements/me'),
  getUnseen: () => api.get<UnseenAchievement[]>('/achievements/unseen'),
  acknowledge: (assignmentIds: string[]) =>
    api.post<{ acknowledged: number }>('/achievements/acknowledge', { assignmentIds }),
  getRoadmap: () => api.get<Roadmap>('/achievements/roadmap'),
};

export const adminAchievementsApi = {
  list: (filter: { tier?: number; isActive?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (filter.tier !== undefined) params.set('tier', String(filter.tier));
    if (filter.isActive !== undefined) params.set('isActive', String(filter.isActive));
    const qs = params.toString();
    return api.get<Achievement[]>(`/admin/achievements${qs ? `?${qs}` : ''}`);
  },
  create: (input: {
    key: string;
    title: string;
    description: string;
    iconUrl?: string | null;
    tier: number;
    order?: number;
    rule: AchievementRule;
    isActive?: boolean;
  }) => api.post<Achievement>('/admin/achievements', input),
  update: (id: string, input: Partial<Omit<Achievement, 'id' | 'key' | 'createdAt' | 'updatedAt'>>) =>
    api.patch<Achievement>(`/admin/achievements/${id}`, input),
  remove: (id: string) => api.delete<void>(`/admin/achievements/${id}`),
  knownEvents: () =>
    api.get<{ events: Array<{ type: string; description: string; payload: string[] }> }>(
      '/admin/achievements/events',
    ),
  runCron: () => api.post<unknown>('/admin/achievements/cron/run', {}),
  reprocessEvents: () =>
    api.post<{ reEnqueued: number }>('/admin/achievements/events/reprocess', {}),
  mentorAction: (
    userId: string,
    body: { type: 'mentor.review.completed' | 'mentor.tier_check.completed'; subject?: string; fromTier?: number },
  ) => api.post<{ ok: boolean }>(`/admin/achievements/users/${userId}/mentor-action`, body),
};
